import { useState } from "react";
import { Send, Check, AlertCircle, CalendarDays } from "lucide-react";
import axios from "axios";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_FORM = {
  name: "",
  phone: "",
  email: "",
  pickup: "",
  destination: "",
  date: "",
  time: "",
  passengers: "1",
  message: "",
};

export default function BookingSection() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date) {
      setForm({ ...form, date: format(date, "dd/MM/yyyy") });
    }
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const formData = { ...form };

    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        passengers: parseInt(formData.passengers, 10),
      });
      setStatus("success");
      setForm(INITIAL_FORM);
      setSelectedDate(null);

      // Professional formatted email notification
      const subject = encodeURIComponent(
        `Warner VTC — Nouvelle demande de réservation`
      );
      const body = encodeURIComponent(
        `Bonjour,\n\n` +
        `Vous avez reçu une nouvelle demande de réservation via le site Warner VTC.\n\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
        `INFORMATIONS CLIENT\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `Nom : ${formData.name}\n` +
        `Téléphone : ${formData.phone}\n` +
        `Email : ${formData.email}\n\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
        `DÉTAILS DU TRAJET\n` +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `Départ : ${formData.pickup}\n` +
        `Destination : ${formData.destination}\n` +
        `Date : ${formData.date}\n` +
        `Heure : ${formData.time}\n` +
        `Nombre de passagers : ${formData.passengers}\n\n` +
        (formData.message
          ? `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
            `MESSAGE\n` +
            `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
            `${formData.message}\n\n`
          : ``) +
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` +
        `Demande envoyée depuis le site Warner VTC.`
      );
      window.open(
        `mailto:warnervtc@gmail.com?subject=${subject}&body=${body}`,
        "_self"
      );
    } catch (err) {
      setStatus("error");
      setErrorMsg("Une erreur est survenue. Veuillez réessayer ou nous appeler directement.");
    }
  };

  return (
    <section
      id="reservation"
      data-testid="booking-section"
      className="relative py-24 lg:py-32 bg-[#131313]"
    >
      {/* Gold glow background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="font-accent italic text-gold text-base tracking-widest mb-4">
            Demande de réservation
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
            Réserver
          </h2>
          <div className="gold-line w-24 mx-auto mt-8" />
          <p className="text-white/50 text-sm mt-6 max-w-md mx-auto tracking-wide font-light">
            Remplissez le formulaire ci-dessous et nous vous recontacterons dans les plus brefs délais pour confirmer votre trajet.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="glass-panel max-w-3xl mx-auto p-8 lg:p-12 gold-pulse"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            {/* Name */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Nom complet
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                data-testid="booking-input-name"
                className="booking-input"
                placeholder="Jean Dupont"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                data-testid="booking-input-phone"
                className="booking-input"
                placeholder="06 12 34 56 78"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                data-testid="booking-input-email"
                className="booking-input"
                placeholder="jean@exemple.fr"
              />
            </div>

            {/* Passengers */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Passagers
              </label>
              <select
                name="passengers"
                value={form.passengers}
                onChange={handleChange}
                data-testid="booking-select-passengers"
                className="booking-select"
              >
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <option key={n} value={n}>
                    {n} passager{n > 1 ? "s" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Pickup */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Lieu de départ
              </label>
              <input
                type="text"
                name="pickup"
                value={form.pickup}
                onChange={handleChange}
                required
                data-testid="booking-input-pickup"
                className="booking-input"
                placeholder="Aéroport de Brest"
              />
            </div>

            {/* Destination */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Destination
              </label>
              <input
                type="text"
                name="destination"
                value={form.destination}
                onChange={handleChange}
                required
                data-testid="booking-input-destination"
                className="booking-input"
                placeholder="Hôtel, adresse..."
              />
            </div>

            {/* Date with Calendar */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Date
              </label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="booking-input-date"
                    className="booking-input text-left flex items-center justify-between w-full cursor-pointer"
                  >
                    <span className={form.date ? "text-white" : "text-[#52525B]"}>
                      {form.date || "Sélectionner une date"}
                    </span>
                    <CalendarDays size={16} className="text-white/30" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    locale={fr}
                    disabled={(date) => date < new Date()}
                    className="bg-[#1A1A1A] text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time */}
            <div>
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Heure
              </label>
              <input
                type="time"
                name="time"
                value={form.time}
                onChange={handleChange}
                required
                data-testid="booking-input-time"
                className="booking-input"
              />
            </div>

            {/* Message - Full width */}
            <div className="md:col-span-2">
              <label className="text-xs text-white/50 uppercase tracking-[0.2em] mb-2 block">
                Message (optionnel)
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={3}
                data-testid="booking-input-message"
                className="booking-input resize-none"
                placeholder="Précisions sur votre trajet, bagages, besoins particuliers..."
              />
            </div>
          </div>

          {/* Submit */}
          <div className="mt-10 flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="booking-submit-button"
              className="w-full sm:w-auto bg-gold text-black font-semibold px-12 py-4 uppercase tracking-[0.2em] text-sm hover:bg-white transition-colors duration-300 flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {status === "loading" ? (
                "Envoi en cours..."
              ) : (
                <>
                  <Send size={16} />
                  Envoyer la demande
                </>
              )}
            </button>

            {status === "success" && (
              <div
                data-testid="booking-success-message"
                className="flex items-center gap-2 text-emerald-400 text-sm"
              >
                <Check size={16} />
                Demande envoyée avec succès ! Nous vous recontacterons rapidement.
              </div>
            )}

            {status === "error" && (
              <div
                data-testid="booking-error-message"
                className="flex items-center gap-2 text-red-400 text-sm"
              >
                <AlertCircle size={16} />
                {errorMsg}
              </div>
            )}
          </div>
        </form>

        {/* Non-conventionné notice */}
        <p className="text-center text-white/20 text-xs mt-8 tracking-wider">
          Service non conventionné — Tarifs sur devis
        </p>
      </div>
    </section>
  );
}
